import { useModalContext } from 'context/EventModalContext'
import React, { useCallback } from 'react'
import Button from 'components/Button'
import { EventQuery, EventRegistrationsQuery } from 'graphql/documents'
import {
  useSetAttendeeRoleMutation,
  EventRegistrationRole,
  EventState,
  EventRegistrationsQueryResult,
  useBanAttendeeMutation
} from 'graphql/generated'
import { useEventVideoContext } from 'context/EventVideoContext'
import { useFlag, useQueryErrorHandler } from 'hooks'
import { useSetSpeaker, useVideoTileOptions } from 'hooks/events'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import DropdownMenu from 'components/common/Dropdown/DropdownMenu'

interface IProps {
  attendee: EventRegistrationsQueryResult['event']['registrations']['edges'][0]['node']
}

function LiveEventAttendeeListItemControls({ attendee }: IProps) {
  const { event, currentSpeakers } = useEventVideoContext()

  const { hideModal } = useModalContext()
  const onError = useQueryErrorHandler(
    'Failed to set speaker. Please try again.'
  )

  const [setSpeaker, settingSpeaker] = useSetSpeaker(
    attendee.user.id,
    hideModal
  )

  const manageSpeaker = useVideoTileOptions({
    id: attendee.user.id,
    name: attendee.user.displayName || attendee.user.id
  })

  const [setRole] = useSetAttendeeRoleMutation({
    refetchQueries: (args: any) =>
      args?.data.setAttendeeRole.role === 'HOST'
        ? [
            {
              query: EventQuery,
              variables: {
                id: event.id
              }
            },
            {
              query: EventRegistrationsQuery,
              variables: {
                id: event.id,
                registrationConnection: {
                  first: 100
                }
              }
            }
          ]
        : [],
    onError,
    onCompleted: hideModal
  })
  const maxSpeakers = useFlag('EVENTS:MAX_SPEAKERS', 4)

  const confirm = useConfirmPromptModal()

  const makeHost = useCallback(
    () =>
      confirm({
        title: 'Change Host',
        body: `Are you sure you want to reassign this event's host?`,
        cancelLabel: 'Nevermind',
        confirmLabel: 'Confirm',
        onClick: () =>
          setRole({
            variables: {
              eventId: event.id,
              userId: attendee.user.id,
              role: EventRegistrationRole.HOST
            }
          })
      }),
    [attendee.user.id, event.id, confirm, setRole]
  )

  const onBanError = useQueryErrorHandler('Failed to ban attendee')
  const [banAttendee, { loading: savingBan }] = useBanAttendeeMutation({
    variables: {
      eventId: event.id,
      userId: attendee.user.id
    },
    onError: onBanError,
    onCompleted: () => hideModal()
  })

  if (!event.isPrivileged) {
    return null
  }

  const canSetRole =
    // the host's role can't be changed directly
    attendee.user.id !== event.user.id &&
    // event must not have started yet
    event.state === EventState.SCHEDULED &&
    // attendee must not be waitlisted
    !attendee.waitlisted

  const eligibleToSpeak =
    // attendee must be online
    attendee.online &&
    // the host may not become the guest speaker
    attendee.user.id !== event.user.id &&
    // event must be in {intro,outro}
    event.state === EventState.HOST_INTRO

  const attendeeIsSpeaking = currentSpeakers?.find(
    x => x.id === attendee.user.id
  )
  const canManageSpeaker =
    eligibleToSpeak &&
    // the <attendee> is a speaker
    attendeeIsSpeaking

  const canAddMoreSpeakers = (currentSpeakers?.length || 0) < maxSpeakers
  const canAddSpeaker =
    eligibleToSpeak &&
    // the <attendee> is not already a speaker
    !attendeeIsSpeaking &&
    // more speakers can be added
    canAddMoreSpeakers

  return (
    <div className="flex flex-row space-x-1.5">
      {canSetRole && (
        <DropdownMenu
          button={<Button size={'xs'} color={'gray'} label={'Change Role'} />}
          items={[
            attendee.role === EventRegistrationRole.GUEST && {
              type: 'button',
              icon: 'user-chart',
              label: `Make Speaker`,
              onClick: () =>
                setRole({
                  variables: {
                    eventId: event.id,
                    userId: attendee.user.id,
                    role: EventRegistrationRole.SPEAKER
                  }
                })
            },
            attendee.role === EventRegistrationRole.SPEAKER && {
              type: 'button',
              icon: 'stop',
              label: `Remove Speaker Role`,
              onClick: () =>
                setRole({
                  variables: {
                    eventId: event.id,
                    userId: attendee.user.id,
                    role: EventRegistrationRole.GUEST
                  }
                })
            },
            {
              type: 'button',
              icon: 'crown',
              label: `Make Host`,
              onClick: makeHost
            }
          ]}
        />
      )}

      {canAddSpeaker && (
        <Button
          size={'xs'}
          color={'gray'}
          label={'Let Speak'}
          onClick={setSpeaker}
          disabled={settingSpeaker}
          loading={settingSpeaker}
          preventDefault
        />
      )}

      {canManageSpeaker && (
        <Button
          size={'xs'}
          color={'gray'}
          label={'Manage Speaker'}
          onClick={manageSpeaker}
          preventDefault
        />
      )}

      <Button
        size={'xs'}
        color={'red'}
        label={'Kick'}
        onClick={() => banAttendee()}
        loading={savingBan}
        preventDefault
      />
    </div>
  )
}

export default LiveEventAttendeeListItemControls
